import React from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import { useTranslation } from 'react-i18next';
import * as Device from 'react-device-detect';
import moment from 'moment';
import { toast } from 'react-toastify';

import { EventStatusEnum, useGetEventDetailedQuery, useUpdateEventStatusMutation } from '@graphql';
import { DialogEditContext } from '../../common/layout';
import { refetchEventsVar } from '@context';
import { useSession } from '@hooks';
import { DeleteEvent } from './DeleteEvent';
import { EventCoverDialog } from '../event-form';

interface EditOptionsProps {
  eventId: string;
}

type MenuActionType =
  | 'edit'
  | 'edit mode'
  | 'see mode'
  | 'ticket'
  | 'faq'
  | 'delete'
  | 'publish'
  | 'cancel'
  | 'cover'
  | 'postpone'
  | 'restore'
  | 'draft'
  | 'duplicate';

const PUBLISH_PAST_TOAST_ID = 'publish-past-blocked';

function EditOptions(props: Readonly<EditOptionsProps>) {
  const context = React.useContext(DialogEditContext);

  if (!context) {
    throw new Error('EditOptions must be used inside DialogEditWrapper');
  }

  const { eventId } = props;
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { account } = useSession();
  const theme = useTheme();
  const { t } = useTranslation('cta');

  const { data } = useGetEventDetailedQuery({
    skip: !eventId || !account,
    variables: { id: eventId },
  });

  const handleDeleted = async () => {
    setConfirmDeleteOpen(false);
  };

  const [updateStatus] = useUpdateEventStatusMutation({
    onCompleted({ updateEventStatus }) {
      refetchEventsVar(true);
      const newStatus = updateEventStatus.status.toLowerCase();
      toast.success(t(`event.toast.status.success.${newStatus}`, { ns: 'field' }));
      newStatus === 'deleted' && handleDeleted();
    },
    onError(error, client) {
      let statusAttempted = client?.variables?.status?.toLowerCase() || 'update';

      if (statusAttempted === 'draft') {
        statusAttempted = status === EventStatusEnum.Published ? 'draft' : 'restore';
      }

      toast.error(t(`event.toast.${statusAttempted}.error`, { ns: 'field' }));
      handleClose();
    },
  });

  const handleUpdateStatus = (newStatus: EventStatusEnum) => {
    updateStatus({
      variables: {
        id: eventId,
        status: newStatus,
      },
    });
  };

  const handleDelete = () => {
    setConfirmDeleteOpen(true);
    handleClose();
  };

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClickItem = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // keep the menu from handling the click globally
    const item = event.currentTarget as HTMLElement; // this is the <MenuItem>
    const type = item.dataset.type as MenuActionType | undefined;
    if (type === 'publish' && moment(from).isBefore()) {
      if (!toast.isActive(PUBLISH_PAST_TOAST_ID)) {
        toast.warning('Impossible de publier un événement dont la date de début est passée.', {
          toastId: PUBLISH_PAST_TOAST_ID,
          position: 'bottom-center',
          autoClose: 2000,
          pauseOnHover: false,
          closeOnClick: true,
          hideProgressBar: true,
        });
      }
      return; // keep menu open
    }

    switch (type) {
      case 'publish':
        handleUpdateStatus(EventStatusEnum.Published);
        break;
      case 'cancel':
        handleUpdateStatus(EventStatusEnum.Canceled);
        break;
      case 'postpone':
        handleUpdateStatus(EventStatusEnum.Postponed);
        break;
      case 'restore':
      case 'draft':
        handleUpdateStatus(EventStatusEnum.Draft);
        break;
      case 'delete':
        handleDelete();
        break;
      case 'edit':
      case 'cover':
      case 'duplicate':
      case 'ticket':
        break;
      default:
        break;
    }
    handleClose(); // close the menu after action
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  if (!data) {
    return null;
  }

  const { status, from, to, covers } = data.event;

  return (
    <>
      {/* TRIGGER */}
      <Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
        <Tooltip title={t('options')}>
          <IconButton
            onClick={handleOpen}
            size="small"
            aria-controls={open ? 'edit-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
          >
            <theme.icons.options fontSize={Device.isMobile ? 'small' : 'medium'} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* MENU */}
      <Menu
        anchorEl={anchorEl}
        id="edit-menu"
        open={open}
        onClose={handleClose}
        slotProps={{
          list: {
            'aria-labelledby': 'edit-button',
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {status !== EventStatusEnum.Draft && covers.length === 0 && (
          <EventCoverDialog trigger={<MenuDisplay type="cover" onClick={() => {}} />} />
        )}
        <MenuDisplay type="duplicate" onClick={handleClickItem} />
        <Divider />
        {status === EventStatusEnum.Draft && (
          <>
            <MenuDisplay type="publish" onClick={handleClickItem} disabled={moment(from).isBefore()} />
            {account?.__typename === 'Spot' && <MenuDisplay type="ticket" onClick={handleClickItem} />}
            <MenuDisplay type="delete" onClick={handleClickItem} />
          </>
        )}
        {status === EventStatusEnum.Published && (
          <>
            {account?.__typename === 'Spot' && <MenuDisplay type="ticket" onClick={handleClickItem} />}
            <MenuDisplay type="postpone" onClick={handleClickItem} />
            <MenuDisplay type="cancel" onClick={handleClickItem} />
          </>
        )}
        {status === EventStatusEnum.Postponed && (
          <>
            <MenuDisplay type="publish" onClick={handleClickItem} disabled={moment(from).isBefore()} />
            <MenuDisplay type="cancel" onClick={handleClickItem} />
            {account?.__typename === 'Spot' && <MenuDisplay type="ticket" onClick={handleClickItem} />}
          </>
        )}
        {status === EventStatusEnum.Canceled && (
          <>
            {/* <MenuDisplay type="publish" onClick={handleClickItem} disabled={moment(from).isBefore()} /> */}
            {/* <MenuDisplay type="edit" onClick={handleClickItem} /> */}
            {/* <MenuDisplay type="cover" onClick={handleClickItem} /> */}
            {/* <MenuDisplay type="duplicate" onClick={handleClickItem} /> */}
          </>
        )}
        {[EventStatusEnum.Deleted, EventStatusEnum.Removed].includes(status) && (
          <>
            <MenuDisplay type="restore" onClick={handleClickItem} />
          </>
        )}
      </Menu>

      {/* DELETE EVENT CONFIRMATION */}
      <DeleteEvent
        open={confirmDeleteOpen}
        setOpen={setConfirmDeleteOpen}
        handleDeleted={handleDeleted}
        id={eventId}
      />
    </>
  );
}

interface MenuDisplayProps {
  onClick: (event: React.MouseEvent<HTMLElement>) => void; // send the event up
  type: MenuActionType;
  disabled?: boolean;
}

const MenuDisplay = (props: MenuDisplayProps) => {
  const { onClick, type, disabled } = props;
  const theme = useTheme();
  const { t } = useTranslation('cta');

  const fontSize = Device.isMobile ? 'small' : 'medium';

  const iconsMap: Record<string, React.ReactNode> = {
    edit: <theme.icons.edit fontSize={fontSize} />,
    'edit mode': <theme.icons.edit fontSize={fontSize} />,
    'see mode': <theme.icons.see fontSize={fontSize} />,
    cover: <theme.icons.image fontSize={fontSize} />,
    delete: <theme.icons.delete fontSize={fontSize} />,
    publish: <theme.icons.publish fontSize={fontSize} />,
    cancel: <theme.icons.draft fontSize={fontSize} />,
    postpone: <theme.icons.postpone fontSize={fontSize} />,
    restore: <theme.icons.restore fontSize={fontSize} />,
    draft: <theme.icons.draft fontSize={fontSize} />,
    ticket: <theme.icons.ticket fontSize={fontSize} />,
    duplicate: <theme.icons.copy fontSize={fontSize} />,
    faq: <theme.icons.faq fontSize={fontSize} />,
  };

  return (
    <MenuItem data-type={type} onClick={onClick} sx={{ opacity: disabled ? 0.5 : 1 }}>
      <ListItemIcon sx={{ color: 'inherit' }}>{iconsMap[type]}</ListItemIcon>
      <Typography component="span" variant={Device.isMobile ? 'body2' : 'body1'}>
        {t(type)}
      </Typography>
    </MenuItem>
  );
};

export default EditOptions;
